import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

const ROOT_KEY = '__root__';

function searchable(value) {
  return String(value || '').toLocaleLowerCase().replace(/[\s/'-]+/g, '');
}

function normalizeItems(items) {
  return (Array.isArray(items) ? items : [])
    .filter(item => item?.id !== undefined && item?.id !== null && item?.name)
    .map(item => ({
      ...item,
      id: String(item.id),
      parent_id: item.parent_id === null || item.parent_id === undefined ? null : String(item.parent_id),
      sort_order: Number(item.sort_order) || 0,
    }));
}

function compareRegions(left, right) {
  return left.sort_order - right.sort_order
    || String(left.name).localeCompare(String(right.name), 'zh-CN')
    || left.id.localeCompare(right.id);
}

export default function RegionCascader({
  items,
  value,
  onChange,
  disabled = false,
  loading = false,
  error = '',
  onRetry,
  english = false,
}) {
  const rootRef = useRef(null);
  const normalizedItems = useMemo(() => normalizeItems(items), [items]);
  const model = useMemo(() => {
    const byId = new Map(normalizedItems.map(item => [item.id, item]));
    const children = new Map();
    normalizedItems.forEach(item => {
      const key = item.parent_id || ROOT_KEY;
      const list = children.get(key) || [];
      list.push(item);
      children.set(key, list);
    });
    children.forEach(list => list.sort(compareRegions));

    const getPath = id => {
      const path = [];
      const visited = new Set();
      let current = byId.get(String(id || ''));
      while (current && !visited.has(current.id)) {
        path.unshift(current);
        visited.add(current.id);
        current = current.parent_id ? byId.get(current.parent_id) : null;
      }
      return path;
    };
    const isLeaf = id => (children.get(String(id)) || []).length === 0;
    return { byId, children, getPath, isLeaf };
  }, [normalizedItems]);

  const selectedPath = useMemo(() => model.getPath(value), [model, value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [browsePath, setBrowsePath] = useState(() => selectedPath.map(item => item.id));

  useEffect(() => {
    if (!open) setBrowsePath(selectedPath.map(item => item.id));
  }, [open, selectedPath]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const browseColumns = useMemo(() => {
    const columns = [model.children.get(ROOT_KEY) || []];
    browsePath.forEach(id => {
      const childItems = model.children.get(id) || [];
      if (childItems.length > 0) columns.push(childItems);
    });
    return columns;
  }, [browsePath, model]);

  const searchResults = useMemo(() => {
    const needle = searchable(query);
    if (!needle) return [];
    return normalizedItems
      .filter(item => {
        if (!model.isLeaf(item.id)) return false;
        const path = model.getPath(item.id);
        return searchable([
          path.map(entry => entry.name).join('/'),
          path.map(entry => entry.short_name).filter(Boolean).join('/'),
          path.map(entry => entry.pinyin).filter(Boolean).join('/'),
          path.map(entry => entry.pinyin_initials).filter(Boolean).join('/'),
          item.code,
        ].join(' ')).includes(needle);
      })
      .slice(0, 80);
  }, [model, normalizedItems, query]);

  const choose = id => {
    if (!model.isLeaf(id)) return;
    onChange?.(id);
    setQuery('');
    setOpen(false);
  };

  const handleBrowse = (item, columnIndex) => {
    const nextPath = [...browsePath.slice(0, columnIndex), item.id];
    setBrowsePath(nextPath);
    if (model.isLeaf(item.id)) choose(item.id);
  };

  const activeItem = model.byId.get(browsePath[browsePath.length - 1]);
  const selectedLabel = selectedPath.map(item => item.name).join(' / ');
  const unavailable = disabled || loading || Boolean(error) || normalizedItems.length === 0;

  return (
    <div className="region-cascader" ref={rootRef}>
      <div className="region-cascader__trigger-row">
        <button
          type="button"
          className="region-cascader__trigger"
          aria-label={english ? 'Country / Region' : '国家 / 地区'}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={unavailable}
          onClick={() => setOpen(current => !current)}
        >
          <span className={selectedLabel ? '' : 'region-cascader__placeholder'}>
            {loading
              ? (english ? 'Loading regions…' : '正在加载地区…')
              : error
                ? ('地区加载失败')
                : selectedLabel || (normalizedItems.length === 0
                  ? (english ? 'No region data' : '暂无地区数据')
                  : (english ? 'Select a region' : '请选择地区'))}
          </span>
          <ChevronDown size={14} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="region-cascader__clear"
            aria-label={english ? 'Clear region' : '清除地区'}
            onClick={() => onChange?.('')}
          >
            <X size={13} />
          </button>
        )}
      </div>


      {open && (
        <div className="region-cascader__panel" role="dialog" aria-label={english ? 'Select a region' : '选择地区'}>
          <label className="region-cascader__search">
            <Search size={14} />
            <input
              autoFocus
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={english ? 'Search name, path, or pinyin' : '搜索名称、完整路径或拼音'}
              aria-label={english ? 'Search regions' : '搜索地区'}
            />
          </label>

          {query.trim() ? (
            <div className="region-cascader__results" role="listbox">
              {searchResults.length > 0 ? searchResults.map(item => {
                const pathLabel = model.getPath(item.id).map(entry => entry.name).join(' / ');
                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(value || '') === item.id}
                    key={item.id}
                    onClick={() => choose(item.id)}
                  >
                    <span>{pathLabel}</span>
                    {String(value || '') === item.id && <Check size={13} />}
                  </button>
                );
              }) : (
                <p>{english ? 'No matching regions' : '没有匹配的地区'}</p>
              )}
            </div>
          ) : (
            <>
              <div className="region-cascader__columns">
                {browseColumns.map((column, columnIndex) => (
                  <div className="region-cascader__column" role="listbox" key={`${columnIndex}-${browsePath[columnIndex - 1] || ROOT_KEY}`}>
                    {column.map(item => {
                      const active = browsePath[columnIndex] === item.id;
                      return (
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={active ? 'is-active' : ''}
                          key={item.id}
                          onClick={() => handleBrowse(item, columnIndex)}
                        >
                          <span>{item.short_name || item.name}</span>
                          {active && <Check size={13} />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
              {activeItem && !model.isLeaf(activeItem.id) && (
                <div className="region-cascader__footer">
                  <span>{model.getPath(activeItem.id).map(item => item.name).join(' / ')}</span>
                  <strong>{english ? 'Continue to the last level' : '请继续选择到最后一级'}</strong>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
